import React, { useRef, useState, useEffect, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import Konva from 'konva';
import {
  FaMousePointer,
  FaDrawPolygon,
  FaEdit,
  FaSearchPlus,
  FaSearchMinus,
  FaExpand,
  FaCompress,
  FaUpload,
  FaImage,
  FaTrash,
  FaArrowRight,
  FaSquare,
  FaRegSquare,
  FaSpinner,
  FaKeyboard
} from 'react-icons/fa';
import {
  selectSelectedObject,
  setEditorMode,
  setSelectedArea,
  addDrawingPoint,
  clearDrawingPoints,
  setStageScale,
  setStagePosition,
  setSelectedObject
} from '../../../actions/infoTerminal';
import {
  fetchImageBlob,
  uploadImageToServer,
  createAreaOnServer,
  updateAreaOnServer,
  deleteAreaFromServer,
  fetchObjectDetails
} from '../../../actions/infoTerminalApi';
import styles from './styles.module.scss';
import ConfirmModal from '../Delete';
import { normalizePoints, denormalizePoints, getCentroid } from '../utils/geometry';

const SchemaEditor = ({
  selectedObject,
  selectedAreaId,
  areasRefreshCount,
  imageRefreshCount,
  allObjects,
  editorMode,
  currentDrawingPoints,
  stageScale,
  stagePosition,
  setEditorMode,
  setSelectedArea,
  addDrawingPoint,
  clearDrawingPoints,
  setStageScale,
  setStagePosition,
  setSelectedObject
}) => {
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const layerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 800, height: 600 });
  const [imageObj, setImageObj] = useState(null);

  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPos, setLastPanPos] = useState({ x: 0, y: 0 });

  const [deleteConfirmArea, setDeleteConfirmArea] = useState(null);
  const [showHotkeys, setShowHotkeys] = useState(false);

  // Состояние для рисования прямоугольника/квадрата
  const [rectStartPoint, setRectStartPoint] = useState(null);
  const [rectCurrentPoint, setRectCurrentPoint] = useState(null);

  // Состояние загрузки изображения и областей с сервера
  const [imageLoading, setImageLoading] = useState(false);
  const [objectDetails, setObjectDetails] = useState(null);
  const [imageBlobUrl, setImageBlobUrl] = useState(null);

  // Вычисляем selectedArea из objectDetails по selectedAreaId
  const selectedArea = objectDetails?.areas?.find(a => a.id === selectedAreaId) || null;

  // Получить имя связанного объекта
  const getLinkedObjectName = useCallback((linkedObjectId) => {
    if (!linkedObjectId) return 'Не привязано';
    const linked = allObjects.find(obj => obj.id === linkedObjectId);
    return linked ? linked.name : 'Не привязано';
  }, [allObjects]);

  const handleDeleteSelectedArea = useCallback(() => {
    if (selectedArea && selectedObject) {
      setDeleteConfirmArea({
        id: selectedArea.id,
        name: `область "${getLinkedObjectName(selectedArea.target_object_id) || 'без названия'}"`
      });
    }
  }, [selectedArea, selectedObject, getLinkedObjectName]);

  const confirmDeleteArea = async () => {
    if (deleteConfirmArea && selectedObject) {
      const success = await dispatch(deleteAreaFromServer(deleteConfirmArea.id));
      if (success) {
        // Перезагружаем детали объекта
        const details = await dispatch(fetchObjectDetails(selectedObject.id));
        if (details) {
          setObjectDetails(details);
        }
        setSelectedArea(null);
      }
      setDeleteConfirmArea(null);
    }
  };

  // Очистка недорисованных фигур при смене режима
  useEffect(() => {
    // Очищаем полигон
    if (editorMode !== 'draw' && currentDrawingPoints.length > 0) {
      clearDrawingPoints();
    }
    // Очищаем прямоугольник/квадрат
    if (editorMode !== 'drawRect' && editorMode !== 'drawSquare') {
      if (rectStartPoint) {
        setRectStartPoint(null);
        setRectCurrentPoint(null);
      }
    }
  }, [editorMode]);

  // Получаем размер контейнера
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight
        });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Инициализация и пересоздание Konva Stage при появлении изображения
  useEffect(() => {
    if (!canvasRef.current) return;

    // Уничтожаем предыдущий stage если есть
    if (stageRef.current) {
      stageRef.current.destroy();
      stageRef.current = null;
      layerRef.current = null;
    }

    const stage = new Konva.Stage({
      container: canvasRef.current,
      width: containerSize.width,
      height: containerSize.height
    });

    const layer = new Konva.Layer();
    stage.add(layer);

    stageRef.current = stage;
    layerRef.current = layer;

    return () => {
      if (stageRef.current) {
        stageRef.current.destroy();
        stageRef.current = null;
        layerRef.current = null;
      }
    };
  }, [selectedObject?.id, selectedObject?.has_image, imageObj]);

  // Обновление размера stage при изменении контейнера
  useEffect(() => {
    if (stageRef.current) {
      stageRef.current.width(containerSize.width);
      stageRef.current.height(containerSize.height);
    }
  }, [containerSize]);

  // Загрузка изображения с сервера при выборе объекта или обновлении изображения
  useEffect(() => {
    let currentBlobUrl = null;

    if (selectedObject?.id && selectedObject?.has_image) {
      setImageObj(null);
      setImageLoading(true);
      dispatch(fetchImageBlob(selectedObject.id, 'original')).then(url => {
        if (url) {
          currentBlobUrl = url;
          setImageBlobUrl(url);
          const img = new window.Image();
          img.src = url;
          img.onload = () => {
            setImageObj(img);
            setImageLoading(false);
          };
          img.onerror = () => {
            setImageLoading(false);
          };
        } else {
          setImageLoading(false);
        }
      });
    } else {
      setImageObj(null);
      setImageBlobUrl(null);
    }

    // Cleanup: освобождаем blob URL при смене объекта или перезагрузке
    return () => {
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [selectedObject?.id, selectedObject?.has_image, imageRefreshCount, dispatch]);

  // Автоматически вписываем изображение в область при загрузке
  useEffect(() => {
    if (imageObj && containerSize.width && containerSize.height) {
      const padding = 20;
      const availableWidth = containerSize.width - padding * 2;
      const availableHeight = containerSize.height - padding * 2;

      const scaleX = availableWidth / imageObj.width;
      const scaleY = availableHeight / imageObj.height;
      const newScale = Math.min(scaleX, scaleY, 5);

      const newX = (containerSize.width - imageObj.width * newScale) / 2;
      const newY = (containerSize.height - imageObj.height * newScale) / 2;

      setStageScale(newScale);
      setStagePosition({ x: newX, y: newY });
    }
  }, [imageObj, selectedObject?.id, setStageScale, setStagePosition]);

  // Загрузка деталей объекта (включая areas) при выборе объекта или области
  useEffect(() => {
    if (selectedObject?.id) {
      dispatch(fetchObjectDetails(selectedObject.id)).then(details => {
        if (details) {
          setObjectDetails(details);
        }
      });
    } else {
      setObjectDetails(null);
    }
  }, [selectedObject?.id, selectedAreaId, areasRefreshCount, dispatch]);

  // Обработка клавиш
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Не обрабатываем, если фокус в input/textarea/select
      const tag = e.target.tagName;
      const isEditable = e.target.isContentEditable;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || isEditable) {
        return;
      }

      if (e.code === 'Escape') {
        if (editorMode === 'draw' && currentDrawingPoints.length > 0) {
          clearDrawingPoints();
        }
        if ((editorMode === 'drawRect' || editorMode === 'drawSquare') && rectStartPoint) {
          setRectStartPoint(null);
          setRectCurrentPoint(null);
        }
        setEditorMode('select');
      }
      if (e.code === 'Delete' && selectedArea && selectedObject) {
        handleDeleteSelectedArea();
      }
      // Горячие клавиши для режимов
      if (e.code === 'KeyV') {
        setEditorMode('select');
      }
      if (e.code === 'KeyD' && selectedObject?.has_image && selectedObject.type !== 'room') {
        setEditorMode('draw');
      }
      if (e.code === 'KeyE' && selectedArea) {
        setEditorMode('edit');
      }
      if (e.code === 'KeyR' && selectedObject?.has_image && selectedObject.type !== 'room') {
        setEditorMode('drawRect');
      }
      if (e.code === 'KeyQ' && selectedObject?.has_image && selectedObject.type !== 'room') {
        setEditorMode('drawSquare');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [editorMode, currentDrawingPoints, selectedArea, selectedObject, rectStartPoint, handleDeleteSelectedArea]);

  // Завершение рисования полигона
  const finishDrawing = useCallback(async () => {
    if (currentDrawingPoints.length >= 3 && selectedObject && imageObj) {
      // Нормализуем координаты в [0..1]
      const normalizedPolygon = normalizePoints(currentDrawingPoints, imageObj.width, imageObj.height);

      // Создаём область на сервере
      const newArea = await dispatch(createAreaOnServer(selectedObject.id, normalizedPolygon, null, ''));

      if (newArea) {
        // Перезагружаем детали объекта
        const details = await dispatch(fetchObjectDetails(selectedObject.id));
        if (details) {
          setObjectDetails(details);
        }
        setSelectedArea(newArea.id);
      }

      clearDrawingPoints();
      setEditorMode('select');
    }
  }, [currentDrawingPoints, selectedObject, imageObj, dispatch, clearDrawingPoints, setEditorMode, setSelectedArea]);

  // Завершение рисования прямоугольника/квадрата
  const finishRectDrawing = useCallback(async (startPoint, endPoint, isSquare) => {
    if (!selectedObject || !startPoint || !endPoint || !imageObj) return;

    let x1 = startPoint.x;
    let y1 = startPoint.y;
    let x2 = endPoint.x;
    let y2 = endPoint.y;

    if (isSquare) {
      const dx = x2 - x1;
      const dy = y2 - y1;
      const size = Math.max(Math.abs(dx), Math.abs(dy));
      x2 = x1 + (dx >= 0 ? size : -size);
      y2 = y1 + (dy >= 0 ? size : -size);
    }

    const points = [
      { x: x1, y: y1 },
      { x: x2, y: y1 },
      { x: x2, y: y2 },
      { x: x1, y: y2 }
    ];

    // Нормализуем координаты в [0..1]
    const normalizedPolygon = normalizePoints(points, imageObj.width, imageObj.height);

    // Создаём область на сервере
    const newArea = await dispatch(createAreaOnServer(selectedObject.id, normalizedPolygon, null, ''));

    if (newArea) {
      // Перезагружаем детали объекта
      const details = await dispatch(fetchObjectDetails(selectedObject.id));
      if (details) {
        setObjectDetails(details);
      }
      setSelectedArea(newArea.id);
    }

    setRectStartPoint(null);
    setRectCurrentPoint(null);
    setEditorMode('select');
  }, [selectedObject, imageObj, dispatch, setSelectedArea, setEditorMode]);

  // Перерисовка canvas
  useEffect(() => {
    if (!layerRef.current || !stageRef.current) return;

    const layer = layerRef.current;

    layer.destroyChildren();

    // Применяем zoom и pan
    layer.scale({ x: stageScale, y: stageScale });
    layer.position(stagePosition);

    // Фоновое изображение
    if (imageObj) {
      const konvaImage = new Konva.Image({
        image: imageObj,
        x: 0,
        y: 0
      });
      layer.add(konvaImage);
    }

    // Существующие области (берём из objectDetails с сервера)
    const areas = objectDetails?.areas || [];
    areas.forEach((area) => {
      const isSelected = selectedArea?.id === area.id;
      // Денормализуем координаты из [0..1] в пиксели
      const denormalizedPoints = imageObj ? denormalizePoints(area.polygon, imageObj.width, imageObj.height) : [];
      const points = denormalizedPoints.flatMap(p => [p.x, p.y]);

      // Полигон
      const polygon = new Konva.Line({
        points,
        closed: true,
        fill: isSelected ? 'rgba(123, 43, 42, 0.3)' : 'rgba(100, 200, 100, 0.25)',
        stroke: isSelected ? '#7b2b2a' : '#4caf50',
        strokeWidth: 2 / stageScale,
        hitStrokeWidth: 10 / stageScale
      });

      polygon.on('click', (e) => {
        if (editorMode === 'select' || editorMode === 'edit') {
          e.cancelBubble = true;
          setSelectedArea(area.id);
        }
      });

      // Drag полигона в режиме edit
      if (editorMode === 'edit' && isSelected) {
        polygon.draggable(true);
        polygon.on('dragend', async () => {
          const dx = polygon.x();
          const dy = polygon.y();
          if ((dx !== 0 || dy !== 0) && imageObj) {
            const updatedPoints = denormalizedPoints.map(p => ({
              x: p.x + dx,
              y: p.y + dy
            }));
            // Нормализуем новые координаты
            const normalizedPolygon = normalizePoints(updatedPoints, imageObj.width, imageObj.height);
            await dispatch(updateAreaOnServer(area.id, { polygon: normalizedPolygon }));
            // Перезагружаем детали
            const details = await dispatch(fetchObjectDetails(selectedObject.id));
            if (details) {
              setObjectDetails(details);
            }
            polygon.position({ x: 0, y: 0 });
          }
        });
      }

      layer.add(polygon);

      // Подпись в центре
      if (denormalizedPoints.length >= 3) {
        const centroid = getCentroid(denormalizedPoints);
        const linkedName = getLinkedObjectName(area.target_object_id);

        const text = new Konva.Text({
          x: centroid.x,
          y: centroid.y,
          text: linkedName,
          fontSize: 12 / stageScale,
          fontFamily: 'Arial',
          fill: '#333',
          align: 'center'
        });
        text.offsetX(text.width() / 2);
        text.offsetY(text.height() / 2);
        layer.add(text);
      }

      // Контрольные точки для редактирования
      if (editorMode === 'edit' && isSelected) {
        denormalizedPoints.forEach((point, idx) => {
          const circle = new Konva.Circle({
            x: point.x,
            y: point.y,
            radius: 6 / stageScale,
            fill: '#fff',
            stroke: '#7b2b2a',
            strokeWidth: 2 / stageScale,
            draggable: true
          });

          circle.on('dragmove', () => {
            const newPoints = [...denormalizedPoints];
            newPoints[idx] = { x: circle.x(), y: circle.y() };
            polygon.points(newPoints.flatMap(p => [p.x, p.y]));
            layer.batchDraw();
          });

          circle.on('dragend', async () => {
            if (!imageObj) return;
            const newPoints = [...denormalizedPoints];
            newPoints[idx] = { x: circle.x(), y: circle.y() };
            // Нормализуем новые координаты
            const normalizedPolygon = normalizePoints(newPoints, imageObj.width, imageObj.height);
            await dispatch(updateAreaOnServer(area.id, { polygon: normalizedPolygon }));
            // Перезагружаем детали
            const details = await dispatch(fetchObjectDetails(selectedObject.id));
            if (details) {
              setObjectDetails(details);
            }
          });

          layer.add(circle);
        });
      }
    });

    // Текущий рисуемый полигон
    if (currentDrawingPoints.length > 0) {
      const linePoints = currentDrawingPoints.flatMap(p => [p.x, p.y]);

      const tempLine = new Konva.Line({
        points: linePoints,
        stroke: '#e91e63',
        strokeWidth: 2 / stageScale,
        dash: [6 / stageScale, 4 / stageScale],
        closed: false
      });
      layer.add(tempLine);

      // Точки
      currentDrawingPoints.forEach((point, idx) => {
        const isFirst = idx === 0;
        const circle = new Konva.Circle({
          x: point.x,
          y: point.y,
          radius: (isFirst ? 8 : 5) / stageScale,
          fill: isFirst ? '#e91e63' : '#fff',
          stroke: '#e91e63',
          strokeWidth: 2 / stageScale
        });

        // Клик по первой точке для замыкания
        if (isFirst && currentDrawingPoints.length >= 3) {
          circle.on('click', (e) => {
            e.cancelBubble = true;
            finishDrawing();
          });
          circle.on('mouseenter', () => {
            circle.scale({ x: 1.3, y: 1.3 });
            layer.batchDraw();
          });
          circle.on('mouseleave', () => {
            circle.scale({ x: 1, y: 1 });
            layer.batchDraw();
          });
        }

        layer.add(circle);
      });
    }

    // Текущий рисуемый прямоугольник/квадрат
    if (rectStartPoint && rectCurrentPoint && (editorMode === 'drawRect' || editorMode === 'drawSquare')) {
      let x1 = rectStartPoint.x;
      let y1 = rectStartPoint.y;
      let x2 = rectCurrentPoint.x;
      let y2 = rectCurrentPoint.y;

      if (editorMode === 'drawSquare') {
        const dx = x2 - x1;
        const dy = y2 - y1;
        const size = Math.max(Math.abs(dx), Math.abs(dy));
        x2 = x1 + (dx >= 0 ? size : -size);
        y2 = y1 + (dy >= 0 ? size : -size);
      }

      const rectPoints = [x1, y1, x2, y1, x2, y2, x1, y2];

      const tempRect = new Konva.Line({
        points: rectPoints,
        closed: true,
        stroke: '#e91e63',
        strokeWidth: 2 / stageScale,
        dash: [6 / stageScale, 4 / stageScale],
        fill: 'rgba(233, 30, 99, 0.1)'
      });
      layer.add(tempRect);
    }

    layer.batchDraw();
  }, [imageObj, selectedObject, selectedArea, editorMode, currentDrawingPoints, stageScale, stagePosition, getLinkedObjectName, finishDrawing, setSelectedArea, rectStartPoint, rectCurrentPoint, objectDetails, dispatch]);

  // Клик по stage
  const handleStageClick = useCallback((e) => {
    if (!stageRef.current) return;

    const stage = stageRef.current;
    const clickedOnEmpty = e.target === stage || e.target.getClassName() === 'Image';

    if (editorMode === 'draw' && selectedObject?.has_image && imageObj) {
      const pos = stage.getPointerPosition();
      const realPos = {
        x: (pos.x - stagePosition.x) / stageScale,
        y: (pos.y - stagePosition.y) / stageScale
      };
      addDrawingPoint(realPos);
    } else if (editorMode === 'select' && clickedOnEmpty) {
      setSelectedArea(null);
    }
  }, [editorMode, selectedObject, stageScale, stagePosition, addDrawingPoint, setSelectedArea, imageObj]);

  // Двойной клик для завершения полигона
  const handleDblClick = useCallback(() => {
    if (editorMode === 'draw' && currentDrawingPoints.length >= 3) {
      finishDrawing();
    }
  }, [editorMode, currentDrawingPoints, finishDrawing]);

  // Zoom колесиком
  const handleWheel = useCallback((e) => {
    e.evt.preventDefault();
    if (!stageRef.current) return;

    const stage = stageRef.current;
    const oldScale = stageScale;
    const pointer = stage.getPointerPosition();

    const scaleBy = 1.15;
    const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
    const clampedScale = Math.min(Math.max(newScale, 0.1), 5);

    const mousePointTo = {
      x: (pointer.x - stagePosition.x) / oldScale,
      y: (pointer.y - stagePosition.y) / oldScale
    };

    const newPos = {
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale
    };

    setStageScale(clampedScale);
    setStagePosition(newPos);
  }, [stageScale, stagePosition, setStageScale, setStagePosition]);

  // Pan
  const handleDragMove = useCallback((e) => {
    if (e.evt.buttons === 4) {
      setStagePosition({
        x: e.target.x(),
        y: e.target.y()
      });
    }
  }, [setStagePosition]);

  // Привязка обработчиков к stage
  useEffect(() => {
  if (!stageRef.current) return;

  const stage = stageRef.current;
  stage.off('click');
  stage.off('dblclick');
  stage.off('wheel');
  stage.off('mousedown');
  stage.off('mousemove');
  stage.off('mouseup');

  stage.on('click', handleStageClick);
  stage.on('dblclick', handleDblClick);
  stage.on('wheel', handleWheel);

  // Панорамирование левой кнопкой (без клавиш)
  stage.on('mousedown', (e) => {
    // Не начинаем панорамирование, если:
    // - режим draw (там свои клики для рисования)
    // - клик по перетаскиваемому полигону (в режиме edit)
    // - клик по контрольной точке (circle)
    const target = e.target;
    const isDraggablePolygon = target.getClassName() === 'Line' && target.draggable();
    const isControlPoint = target.getClassName() === 'Circle';
    const isInDrawMode = editorMode === 'draw';
    const isInRectMode = editorMode === 'drawRect' || editorMode === 'drawSquare';

    if (isInDrawMode || isDraggablePolygon || isControlPoint) {
      return; // не мешаем другим действиям
    }

    // Начинаем рисование прямоугольника/квадрата
    if (isInRectMode && selectedObject?.has_image && imageObj) {
      const pos = stage.getPointerPosition();
      const realPos = {
        x: (pos.x - stagePosition.x) / stageScale,
        y: (pos.y - stagePosition.y) / stageScale
      };
      setRectStartPoint(realPos);
      setRectCurrentPoint(realPos);
      return;
    }

    // Начинаем панорамирование
    setIsPanning(true);
    const pos = stage.getPointerPosition();
    setLastPanPos({ x: pos.x, y: pos.y });
    stage.container().style.cursor = 'grabbing';
  });

  stage.on('mousemove', (e) => {
    // Обновление позиции при рисовании прямоугольника
    if (rectStartPoint && (editorMode === 'drawRect' || editorMode === 'drawSquare')) {
      const pos = stage.getPointerPosition();
      const realPos = {
        x: (pos.x - stagePosition.x) / stageScale,
        y: (pos.y - stagePosition.y) / stageScale
      };
      setRectCurrentPoint(realPos);
      return;
    }

    if (!isPanning) return;
    const pos = stage.getPointerPosition();
    const dx = pos.x - lastPanPos.x;
    const dy = pos.y - lastPanPos.y;
    const newPos = {
      x: stagePosition.x + dx,
      y: stagePosition.y + dy
    };
    setStagePosition(newPos);
    setLastPanPos({ x: pos.x, y: pos.y });
  });

  stage.on('mouseup', () => {
    // Завершение рисования прямоугольника
    if (rectStartPoint && rectCurrentPoint && (editorMode === 'drawRect' || editorMode === 'drawSquare')) {
      finishRectDrawing(rectStartPoint, rectCurrentPoint, editorMode === 'drawSquare');
      return;
    }

    if (isPanning) {
      setIsPanning(false);
      stage.container().style.cursor = 'default';
    }
  });
}, [handleStageClick, handleDblClick, handleWheel, editorMode, isPanning, lastPanPos, stagePosition, stageScale, setStagePosition, selectedObject, rectStartPoint, rectCurrentPoint, setRectStartPoint, setRectCurrentPoint, finishRectDrawing, imageObj]);

  // Zoom кнопки
  const zoomIn = () => {
    const newScale = Math.min(stageScale * 1.2, 5);
    setStageScale(newScale);
  };

  const zoomOut = () => {
    const newScale = Math.max(stageScale / 1.2, 0.1);
    setStageScale(newScale);
  };

  const resetView = () => {
    setStageScale(1);
    setStagePosition({ x: 0, y: 0 });
  };

  // Вписать изображение в область (Fit to view)
  const fitToView = () => {
    if (!imageObj || !containerSize.width || !containerSize.height) return;

    const padding = 20; // Отступ от краёв
    const availableWidth = containerSize.width - padding * 2;
    const availableHeight = containerSize.height - padding * 2;

    // Вычисляем масштаб, чтобы изображение вписалось целиком
    const scaleX = availableWidth / imageObj.width;
    const scaleY = availableHeight / imageObj.height;
    const newScale = Math.min(scaleX, scaleY, 5); // Не больше максимального масштаба

    // Центрируем изображение
    const newX = (containerSize.width - imageObj.width * newScale) / 2;
    const newY = (containerSize.height - imageObj.height * newScale) / 2;

    setStageScale(newScale);
    setStagePosition({ x: newX, y: newY });
  };

  // Загрузка изображения
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file && selectedObject) {
      setImageLoading(true);
      const result = await dispatch(uploadImageToServer(selectedObject.id, file));
      if (result) {
        // Перезагружаем изображение
        const url = await dispatch(fetchImageBlob(selectedObject.id, 'original'));
        if (url) {
          setImageBlobUrl(url);
          const img = new window.Image();
          img.src = url;
          img.onload = () => {
            setImageObj(img);
            setImageLoading(false);
          };
        }
        // Загружаем детали объекта
        const details = await dispatch(fetchObjectDetails(selectedObject.id));
        if (details) {
          setObjectDetails(details);
        }
      } else {
        setImageLoading(false);
      }
    }
    e.target.value = '';
  };

  const cursorClass = editorMode === 'draw' ? styles.drawMode :
                      editorMode === 'drawRect' ? styles.drawMode :
                      editorMode === 'drawSquare' ? styles.drawMode :
                      editorMode === 'edit' ? styles.editMode : '';

  const showCanvas = selectedObject && selectedObject.has_image && imageObj;

  const handleNavigateToLinkedObject = () => {
    if (selectedArea?.target_object_id) {
      setSelectedObject(selectedArea.target_object_id);
      setSelectedArea(null);
    }
  };

  return (
    <div className={styles.editorContainer}>
      <ConfirmModal
        isOpen={!!deleteConfirmArea}
        title="Подтверждение удаления"
        message={
            <>
              Вы уверены, что хотите удалить <strong>{deleteConfirmArea?.name}</strong>?
            </>
          }
        onConfirm={confirmDeleteArea}
        onCancel={() => setDeleteConfirmArea(null)}
      />
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolGroup}>
          <span className={styles.toolLabel}>Режим:</span>
          <button
            className={`${styles.toolBtn} ${editorMode === 'select' ? styles.active : ''}`}
            onClick={() => setEditorMode('select')}
            title="Выбор (V)"
          >
            <FaMousePointer size={14} />
          </button>
          <button
            className={`${styles.toolBtn} ${editorMode === 'edit' ? styles.active : ''}`}
            onClick={() => setEditorMode('edit')}
            disabled={!selectedArea}
            title="Редактирование (E)"
          >
            <FaEdit size={14} />
          </button>
          <button
            className={`${styles.toolBtn} ${editorMode === 'draw' ? styles.active : ''}`}
            onClick={() => setEditorMode('draw')}
            disabled={!selectedObject?.has_image || selectedObject.type === 'room'}
            title="Рисование (D)"
          >
            <FaDrawPolygon size={14} />
          </button>
          <button
            className={`${styles.toolBtn} ${editorMode === 'drawRect' ? styles.active : ''}`}
            onClick={() => setEditorMode('drawRect')}
            disabled={!selectedObject?.has_image || selectedObject.type === 'room'}
            title="Прямоугольник (R)"
          >
            <FaRegSquare size={14} />
          </button>
          <button
            className={`${styles.toolBtn} ${editorMode === 'drawSquare' ? styles.active : ''}`}
            onClick={() => setEditorMode('drawSquare')}
            disabled={!selectedObject?.has_image || selectedObject.type === 'room'}
            title="Квадрат (Q)"
          >
            <FaSquare size={14} />
          </button>
        </div>

        <div className={styles.toolGroup}>
          <button
            className={styles.toolBtn}
            onClick={handleDeleteSelectedArea}
            disabled={!selectedArea}
            title="Удалить область (Delete)"
          >
            <FaTrash size={14} />
          </button>
        </div>

        <div className={styles.toolGroup}>
          <button
            className={`${styles.toolBtn} ${showHotkeys ? styles.active : ''}`}
            onClick={() => setShowHotkeys(!showHotkeys)}
            title="Горячие клавиши"
          >
            <FaKeyboard size={14} />
          </button>
        </div>
        
        {selectedArea?.target_object_id && (
          <button
            className={styles.navigateBtn}
            onClick={handleNavigateToLinkedObject}
            title="Перейти к привязанному объекту"
          >
            <FaArrowRight size={12} />
            <span>Перейти к объекту</span>
          </button>
        )}


        <span className={styles.zoomInfo}>
          {Math.round(stageScale * 100)}%
        </span>
      </div>

      {/* Панель горячих клавиш */}
      {showHotkeys && (
        <div className={styles.hotkeysPanel}>
          <div className={styles.hotkeyItem}><kbd>V</kbd> Выбор</div>
          <div className={styles.hotkeyItem}><kbd>D</kbd> Полигон</div>
          <div className={styles.hotkeyItem}><kbd>R</kbd> Прямоугольник</div>
          <div className={styles.hotkeyItem}><kbd>Q</kbd> Квадрат</div>
          <div className={styles.hotkeyItem}><kbd>E</kbd> Редактирование</div>
          <div className={styles.hotkeyItem}><kbd>Del</kbd> Удалить область</div>
          <div className={styles.hotkeyItem}><kbd>Ctrl+S</kbd> Сохранить</div>
        </div>
      )}

      {/* Canvas container */}
      <div
        ref={containerRef}
        className={`${styles.canvasContainer} ${cursorClass}`}
      >
        {!selectedObject ? (
          <div className={styles.emptyCanvas}>
            <FaImage />
            <p>Выберите объект в дереве слева</p>
          </div>
        ) : imageLoading ? (
          <div className={styles.emptyCanvas}>
            <FaSpinner className={styles.spinner} />
            <p>Загрузка изображения...</p>
          </div>
        ) : !selectedObject.has_image ? (
          <div className={styles.emptyCanvas}>
            <FaUpload />
            <p>Загрузите изображение схемы</p>
            <small>Поддерживаются PNG, JPG, WEBP</small>
            <label className={styles.uploadBtn}>
              <FaUpload size={14} />
              <span>Загрузить</span>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                onChange={handleImageUpload}
              />
            </label>
          </div>
        ) : null}

        {/* Konva canvas */}
        {showCanvas && (
          <div
            ref={canvasRef}
            className={styles.canvasWrapper}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          />
        )}

        {/* Drawing hint */}
        {editorMode === 'draw' && selectedObject?.has_image && imageObj && (
          <div className={styles.drawingHint}>
            {currentDrawingPoints.length === 0
              ? 'Кликните, чтобы начать рисовать полигон'
              : currentDrawingPoints.length < 3
              ? `Добавьте ещё ${3 - currentDrawingPoints.length} точки`
              : 'Кликните на первую точку или двойной клик для завершения'}
          </div>
        )}

        {editorMode === 'drawRect' && selectedObject?.has_image && imageObj && (
          <div className={styles.drawingHint}>
            {!rectStartPoint
              ? 'Зажмите кнопку мыши и перетащите для рисования прямоугольника'
              : 'Отпустите кнопку мыши для завершения'}
          </div>
        )}

        {editorMode === 'drawSquare' && selectedObject?.has_image && imageObj && (
          <div className={styles.drawingHint}>
            {!rectStartPoint
              ? 'Зажмите кнопку мыши и перетащите для рисования квадрата'
              : 'Отпустите кнопку мыши для завершения'}
          </div>
        )}

        {/* Zoom controls */}
        {selectedObject?.has_image && imageObj && (
          <div className={styles.controls}>
            <button className={styles.controlBtn} onClick={zoomIn} title="Приблизить">
              <FaSearchPlus size={14} />
            </button>
            <button className={styles.controlBtn} onClick={zoomOut} title="Отдалить">
              <FaSearchMinus size={14} />
            </button>
            <button className={styles.controlBtn} onClick={fitToView} title="Вписать в область">
              <FaCompress size={14} />
            </button>
            <button className={styles.controlBtn} onClick={resetView} title="Сбросить вид (100%)">
              <FaExpand size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  selectedObject: selectSelectedObject(state),
  selectedAreaId: state.infoTerminal.selectedAreaId,
  areasRefreshCount: state.infoTerminal.areasRefreshCount,
  imageRefreshCount: state.infoTerminal.imageRefreshCount,
  allObjects: state.infoTerminal.objects,
  editorMode: state.infoTerminal.editorMode,
  currentDrawingPoints: state.infoTerminal.currentDrawingPoints,
  stageScale: state.infoTerminal.stageScale,
  stagePosition: state.infoTerminal.stagePosition
});

export default connect(mapStateToProps, {
  setEditorMode,
  setSelectedArea,
  addDrawingPoint,
  clearDrawingPoints,
  setStageScale,
  setStagePosition,
  setSelectedObject
})(SchemaEditor);
